import L from "leaflet";
import type { LonLatBounds } from "../lib/mercator";

/**
 * Manages one Leaflet ImageOverlay per preloaded radar frame and switches
 * which one is visible. All frames are added to the map up front (already
 * preloaded as HTMLImageElement before this is called) and only opacity is
 * toggled, so switching frames is instant with no flicker/reflow.
 *
 * M003: a single RadarLayer instance is shared across regions (one map, one
 * layer) — `setBounds()` changes what NEW frames (addFrame) are anchored to,
 * `hideCurrent()`/`revoke()` are the region-switch primitives (DESIGN.md
 * Aneks M003 §R2): switching away only hides the visible overlay (opacity 0)
 * and leaves every other frame's overlay attached-but-invisible in place, so
 * switching back before a `revoke()` call is instant, from memory, with no
 * refetch. `revoke()` is called once the new region is confirmed ready, to
 * free the old region's blob URLs.
 */
export class RadarLayer {
  private readonly map: L.Map;
  private bounds: L.LatLngBounds;
  private readonly overlays = new Map<number, { overlay: L.ImageOverlay; url: string }>();
  private currentTimeMs: number | null = null;
  private opacity: number;

  constructor(map: L.Map, initialOpacity: number, initialBounds: LonLatBounds) {
    this.map = map;
    this.opacity = initialOpacity;
    this.bounds = toLatLngBounds(initialBounds);
  }

  /** Anchor bounds for FRAMES ADDED FROM NOW ON (region switch) — existing overlays keep whatever bounds they were created with. */
  setBounds(bounds: LonLatBounds): void {
    this.bounds = toLatLngBounds(bounds);
  }

  addFrame(timeMs: number, imageUrl: string): void {
    if (this.overlays.has(timeMs)) {
      // Same timestamp already attached (e.g. switch-back refetch while the
      // old region's overlays were deliberately kept) — the freshly fetched
      // blob would otherwise leak (reviewer HIGH-1 secondary finding).
      releaseUrl(imageUrl);
      return;
    }
    const overlay = L.imageOverlay(imageUrl, this.bounds, {
      opacity: 0,
      interactive: false,
      pane: "overlayPane"
    });
    overlay.addTo(this.map);
    this.overlays.set(timeMs, { overlay, url: imageUrl });
  }

  removeFrame(timeMs: number): void {
    const entry = this.overlays.get(timeMs);
    if (!entry) return;
    this.map.removeLayer(entry.overlay);
    releaseUrl(entry.url);
    this.overlays.delete(timeMs);
    if (this.currentTimeMs === timeMs) {
      this.currentTimeMs = null;
    }
  }

  hasFrame(timeMs: number): boolean {
    return this.overlays.has(timeMs);
  }

  showFrame(timeMs: number): void {
    if (this.currentTimeMs === timeMs) return;
    if (this.currentTimeMs !== null) {
      this.overlays.get(this.currentTimeMs)?.overlay.setOpacity(0);
    }
    const next = this.overlays.get(timeMs);
    if (next) {
      next.overlay.setOpacity(this.opacity);
      this.currentTimeMs = timeMs;
    }
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity;
    if (this.currentTimeMs !== null) {
      this.overlays.get(this.currentTimeMs)?.overlay.setOpacity(opacity);
    }
  }

  getOpacity(): number {
    return this.opacity;
  }

  /** Hide whatever is currently shown without removing/revoking it (region-switch step 2: "old overlay removed" — see class docs). A no-op when nothing is shown. */
  hideCurrent(): void {
    if (this.currentTimeMs !== null) {
      this.overlays.get(this.currentTimeMs)?.overlay.setOpacity(0);
      this.currentTimeMs = null;
    }
  }

  /** Remove+revoke a specific set of frames (region-switch step 6: free the PREVIOUS region's blobs once the new one is ready). Unlike `clear()`, leaves every other region's frames untouched. */
  revoke(timesMs: number[]): void {
    for (const t of timesMs) this.removeFrame(t);
  }

  /** Remove all frames (used on cold-boot init so a reload never leaks old overlays). */
  clear(): void {
    for (const entry of this.overlays.values()) {
      this.map.removeLayer(entry.overlay);
      releaseUrl(entry.url);
    }
    this.overlays.clear();
    this.currentTimeMs = null;
  }

  destroy(): void {
    this.clear();
  }
}

function releaseUrl(url: string): void {
  if (url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

function toLatLngBounds(bounds: LonLatBounds): L.LatLngBounds {
  return L.latLngBounds([bounds.south, bounds.west], [bounds.north, bounds.east]);
}
